// ========================================
// useGratitudes Hook
// ========================================
// Custom hook for managing gratitude list

import { useState, useEffect } from 'react';
import { useAuth } from '../components/Auth/AuthContext';
import {
  getGratitudes,
  createGratitude,
  updateGratitude,
  deleteGratitude,
} from '../firebase/firestore';
import { Gratitude } from '../types';

export const useGratitudes = () => {
  const { currentUser } = useAuth();
  const [gratitudes, setGratitudes] = useState<Gratitude[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load gratitudes
  useEffect(() => {
    if (!currentUser) {
      setGratitudes([]);
      setLoading(false);
      return;
    }

    const loadGratitudes = async () => {
      try {
        setLoading(true);
        const data = await getGratitudes(currentUser.uid);
        setGratitudes(data);
        setError(null);
      } catch (err) {
        console.error('Error loading gratitudes:', err);
        setError('Failed to load gratitudes');
      } finally {
        setLoading(false);
      }
    };

    loadGratitudes();
  }, [currentUser]);

  // Add new gratitude
  const addGratitude = async (item: string, notes?: string) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      await createGratitude(currentUser.uid, { item, notes });

      // Refresh gratitudes list
      const updatedGratitudes = await getGratitudes(currentUser.uid);
      setGratitudes(updatedGratitudes);
    } catch (err) {
      console.error('Error adding gratitude:', err);
      throw err;
    }
  };

  // Update existing gratitude
  const editGratitude = async (id: string, updates: Partial<Gratitude>) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      await updateGratitude(currentUser.uid, id, updates);

      // Refresh gratitudes list
      const updatedGratitudes = await getGratitudes(currentUser.uid);
      setGratitudes(updatedGratitudes);
    } catch (err) {
      console.error('Error updating gratitude:', err);
      throw err;
    }
  };

  // Delete gratitude
  const removeGratitude = async (id: string) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      await deleteGratitude(currentUser.uid, id);

      // Refresh gratitudes list
      const updatedGratitudes = await getGratitudes(currentUser.uid);
      setGratitudes(updatedGratitudes);
    } catch (err) {
      console.error('Error deleting gratitude:', err);
      throw err;
    }
  };

  return {
    gratitudes,
    loading,
    error,
    addGratitude,
    editGratitude,
    removeGratitude,
  };
};
